// Catalogo de planos do painel: a unica fonte de verdade da UI de billing.
//
// Os Price IDs vem do env publico (VITE_STRIPE_PRICE_*). Apenas Price IDs e a
// publishable key podem ser publicos; segredos (secret key, webhook secret) NUNCA
// chegam ao bundle. Se um Price ID nao estiver configurado, o CTA daquele intervalo
// fica desabilitado com aviso de "nao configurado", em vez de quebrar a pagina.
//
// Os precos exibidos sao apenas rotulos para a UI. A cobranca real e do preco
// cadastrado no Stripe; ajustar valor no dashboard nao exige mexer aqui, desde que
// o Price ID continue o mesmo.

import { planTierLabel, type Plan } from "@lucralink/shared";

export type BillingInterval = "month" | "year";

/** Um plano pago exibido na pagina de planos, com seus dois intervalos. */
export interface PlanCatalogEntry {
  readonly plan: Extract<Plan, "pro" | "pro_ai">;
  readonly name: string;
  readonly tagline: string;
  /** Beneficios listados no card, em pt-BR. */
  readonly features: readonly string[];
  /** Marca o tier como o mais recomendado (destaque visual). */
  readonly highlighted: boolean;
  readonly prices: Readonly<Record<BillingInterval, PlanPrice>>;
}

/** Preco de um intervalo: rotulo + Price ID do Stripe (pode faltar no env). */
export interface PlanPrice {
  /** Valor formatado para exibicao, ex.: "R$ 49,90". */
  readonly amountLabel: string;
  /** Sufixo do periodo, ex.: "/mes" ou "/ano". */
  readonly periodLabel: string;
  /** Economia anual exibida sob o preco, quando houver. null = sem rotulo. */
  readonly savingsLabel: string | null;
  /** Percentual de economia no plano anual, ex.: "20%". null para mensal. */
  readonly savingsPercent: string | null;
  /** Valor mensal equivalente no plano anual, ex.: "~R$ 39,92/mes". null para mensal. */
  readonly monthlyEquivalentLabel: string | null;
  /** Stripe Price ID. null/"" quando o env nao foi preenchido. */
  readonly priceId: string | null;
}

/** Le um Price ID do env publico, normalizando vazio/placeholder para null. */
function readPriceId(key: string): string | null {
  const raw = import.meta.env[key] as string | undefined;
  if (!raw) {
    return null;
  }
  const trimmed = raw.trim();
  // Placeholders do .env.example (ex.: "price_<pro-mensal>") nao sao Price IDs
  // validos: o Stripe so aceita ids que comecam com "price_" sem "<".
  if (trimmed === "" || trimmed.includes("<")) {
    return null;
  }
  return trimmed;
}

/** Precos padrao (R$) quando o env nao define VITE_PLAN_PRICE_*. A cobranca real
 *  e sempre a do Price ID no Stripe; isto e so o rotulo exibido. */
export const DEFAULT_PLAN_PRICES = Object.freeze({
  pro: { month: 59.9, year: 599 },
  pro_ai: { month: 89.9, year: 899 },
});

type PaidPlan = keyof typeof DEFAULT_PLAN_PRICES;

const PRICE_ENV_KEY: Readonly<Record<PaidPlan, Record<BillingInterval, string>>> = {
  pro: { month: "VITE_PLAN_PRICE_PRO_MONTH", year: "VITE_PLAN_PRICE_PRO_YEAR" },
  pro_ai: { month: "VITE_PLAN_PRICE_ELITE_MONTH", year: "VITE_PLAN_PRICE_ELITE_YEAR" },
};

const PRICE_ID_ENV_KEY: Readonly<Record<PaidPlan, Record<BillingInterval, string>>> = {
  pro: { month: "VITE_STRIPE_PRICE_PRO_MONTH", year: "VITE_STRIPE_PRICE_PRO_YEAR" },
  pro_ai: { month: "VITE_STRIPE_PRICE_PRO_AI_MONTH", year: "VITE_STRIPE_PRICE_PRO_AI_YEAR" },
};

/** Le um valor em reais do env ("59,90" ou "59.90"). Invalido/ausente = fallback. */
export function parsePriceEnv(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const trimmed = raw.trim();
  // Formato brasileiro ("1.299,90"): ponto e milhar, virgula e decimal.
  const normalized = trimmed.includes(",") ? trimmed.replace(/\./g, "").replace(",", ".") : trimmed;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 && value < 100_000 ? value : fallback;
}

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

/** "R$ 59,90" (espaco normal, sem o NBSP do Intl, para casar com o resto da UI). */
export function formatBRL(value: number): string {
  return BRL.format(value).replace(/\u00a0/g, " ");
}

function readPlanPrice(plan: PaidPlan, interval: BillingInterval): number {
  const raw = import.meta.env[PRICE_ENV_KEY[plan][interval]] as string | undefined;
  return parsePriceEnv(raw, DEFAULT_PLAN_PRICES[plan][interval]);
}

/** Monta os dois intervalos de um plano, calculando a economia do anual. */
export function buildPlanPrices(plan: PaidPlan): Readonly<Record<BillingInterval, PlanPrice>> {
  const month = readPlanPrice(plan, "month");
  const year = readPlanPrice(plan, "year");
  const savings = Math.round((1 - year / (month * 12)) * 100);
  const hasSavings = savings > 0;
  return {
    month: {
      amountLabel: formatBRL(month),
      periodLabel: "/mês",
      savingsLabel: null,
      savingsPercent: null,
      monthlyEquivalentLabel: null,
      priceId: readPriceId(PRICE_ID_ENV_KEY[plan].month),
    },
    year: {
      amountLabel: formatBRL(year),
      periodLabel: "/ano",
      savingsLabel: hasSavings ? "Economia no plano anual" : null,
      savingsPercent: hasSavings ? `${savings}%` : null,
      monthlyEquivalentLabel: `~${formatBRL(year / 12)}/mês`,
      priceId: readPriceId(PRICE_ID_ENV_KEY[plan].year),
    },
  };
}

/** Monta o catalogo de planos pagos (PRO e ELITE) a partir do env. */
export function buildPlanCatalog(): readonly PlanCatalogEntry[] {
  return [
    {
      plan: "pro",
      name: "PRO",
      tagline: "Automação completa para quem vive de divulgar ofertas.",
      highlighted: true,
      features: [
        "Até 10 destinos e origens ilimitadas",
        "Analytics completo e comissões",
        "Vitrine, link na bio e encurtador",
        "Disparos em massa e templates próprios",
        "Marca d'água e links pela extensão",
        "5 imagens com IA",
      ],
      prices: buildPlanPrices("pro"),
    },
    {
      plan: "pro_ai",
      name: "ELITE",
      tagline: "Tudo do PRO, com criativos de IA todo mês.",
      highlighted: false,
      features: [
        "Tudo do plano PRO",
        "50 imagens com IA por mês",
        "Prompt personalizado nas imagens",
      ],
      prices: buildPlanPrices("pro_ai"),
    },
  ];
}

/** Descricao do tier Free, exibida ao lado dos pagos como referencia. */
export const FREE_PLAN_SUMMARY = {
  name: "Free",
  tagline: "Para começar a converter e automatizar sem pagar nada.",
  features: [
    "Links ilimitados nas lojas suportadas",
    "Automação com 1 origem e 1 destino",
    "10 promoções automáticas por dia",
    "Bot pessoal no Telegram",
    "IA de descrição com sua chave Gemini",
  ],
} as const;

export type ComparisonValue = boolean | string;

export interface ComparisonRow {
  readonly label: string;
  readonly free: ComparisonValue;
  readonly pro: ComparisonValue;
  readonly elite: ComparisonValue;
}

/** Tabela comparativa da pagina de planos (textos proprios). Espelha
 *  packages/shared/src/entitlements.ts; os limites reais vivem la e no SQL. */
export const PLAN_COMPARISON: readonly ComparisonRow[] = [
  { label: "Links de afiliado", free: "Ilimitados", pro: "Ilimitados", elite: "Ilimitados" },
  { label: "Contas conectadas (WhatsApp e Telegram)", free: "1 de cada", pro: "1 de cada", elite: "1 de cada" },
  { label: "Origens monitoradas", free: "1", pro: "Ilimitadas", elite: "Ilimitadas" },
  { label: "Destinos do encaminhamento", free: "1", pro: "Até 10", elite: "Até 10" },
  { label: "Promoções automáticas por dia", free: "10", pro: "Ilimitadas", elite: "Ilimitadas" },
  { label: "Bot pessoal no Telegram", free: true, pro: true, elite: true },
  { label: "IA de descrição", free: "Com sua chave", pro: true, elite: true },
  { label: "Analytics completo e comissões", free: false, pro: true, elite: true },
  { label: "Vitrine e link na bio", free: false, pro: true, elite: true },
  { label: "Encurtador avulso", free: false, pro: true, elite: true },
  { label: "Sessão do Mercado Livre pela extensão", free: true, pro: true, elite: true },
  { label: "Gerar links pela extensão", free: false, pro: true, elite: true },
  { label: "Disparos em massa", free: false, pro: true, elite: true },
  { label: "Templates de texto próprios", free: false, pro: true, elite: true },
  { label: "Marca d'água nas imagens", free: false, pro: true, elite: true },
  { label: "Imagens com IA", free: false, pro: "5 no total", elite: "50 por mês" },
];

/** Rotulo comercial de cada tier para badges e cabecalhos (pro_ai = ELITE). */
export function planLabel(plan: Plan | string): string {
  return planTierLabel(plan);
}

/** Indica se o tier e pago (PRO ou ELITE), para estilizar o badge. */
export function isPaidPlan(plan: Plan | string): boolean {
  return plan === "pro" || plan === "pro_ai";
}
